import { productDumpApi } from 'apiConnector/productDump';
import { Advisory } from 'components/common-form/sidePanel/advisory';
import { ConnectedBackTest } from 'components/common-form/sidePanel/backTest/BackTest.connect';
import { BackTestHeaderStatus } from 'components/common-form/sidePanel/backTest/BackTestHeaderStatus';
import { TradeWay } from 'components/common-form/sidePanel/tradeWay/TradeWay';
import { MissedDealFeedback } from 'components/missed-deal-feedback';
import { FC } from 'react';

import { GenericErrorBoundary } from '../builder/form/GenericErrorBoundary';
import { ProductDumpApiContext } from '../contexts/ProductDumpContext';
import { GreeksFeesMetricsTabContent } from './info/QuoteInfos/GreeksFeesMetricsTabContent';
import { MainTabContent } from './MainTabContent';
import { Regulatory } from './regulatory/Regulatory';
import { RegulationTrafficLight } from './regulatory/RegulatoryTrafficLight.connect';
import type { SidePanelProps } from './SidePanel.connect';
import { connectSidePanel } from './SidePanel.connect';
import { SidePanelSkeleton } from './SidePanel.skeleton';
import { Status } from './status/Status';
import { TopTabs } from './topTabs';

export const Component: FC<SidePanelProps> = ({
  activeTab,
  changeTabInSidePanel,
  displayAdvisoryTab,
  hasRegulation,
  hasMissedDealFeedback,
  hasGreeks,
  hasFees,
  hasMetrics,
  hasBackTest,
  quoteId,
  isNotReady,
  isForBooking,
  tabId,
  workspaceType,
}) => {
  if (isNotReady) {
    return <SidePanelSkeleton />;
  }

  return (
    <ProductDumpApiContext.Provider value={productDumpApi}>
      <TopTabs
        activeTab={activeTab}
        handleChangeOfActiveTab={changeTabInSidePanel}
        hasRegulatory={hasRegulation}
        regulatoryAdditionalHeaderContent={<RegulationTrafficLight tabId={tabId} workspaceType={workspaceType} />}
        backTestAdditionalHeaderContent={<BackTestHeaderStatus formId={tabId} />}
        hasAdvisory={displayAdvisoryTab}
        hasMissedDealFeedBack={hasMissedDealFeedback}
        hasGreeks={hasGreeks}
        hasFees={hasFees}
        hasMetrics={hasMetrics}
        hasBackTest={hasBackTest}
        isForBooking={isForBooking}
        summaryTabContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <div className="d-flex flex-column h-100">
              <Status tabId={tabId} workspaceType={workspaceType} />
              <TradeWay tabId={tabId} workspaceType={workspaceType} />
              <MainTabContent className="overflow-y-auto flex-grow-1" tabId={tabId} workspaceType={workspaceType} />
            </div>
          </GenericErrorBoundary>
        }
        regulatoryTabContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <Status tabId={tabId} workspaceType={workspaceType} />
            <TradeWay tabId={tabId} workspaceType={workspaceType} />
            <Regulatory tabId={tabId} workspaceType={workspaceType} />
          </GenericErrorBoundary>
        }
        advisoryTabContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <Status tabId={tabId} workspaceType={workspaceType} />
            <TradeWay tabId={tabId} workspaceType={workspaceType} />
            {quoteId && <Advisory tabId={tabId} quoteId={quoteId} />}
          </GenericErrorBoundary>
        }
        backTestTabContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <Status tabId={tabId} workspaceType={workspaceType} />
            <TradeWay tabId={tabId} workspaceType={workspaceType} />
            {quoteId && <ConnectedBackTest formId={tabId} />}
          </GenericErrorBoundary>
        }
        missedDealFeedBackContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <Status tabId={tabId} workspaceType={workspaceType} />
            <TradeWay tabId={tabId} workspaceType={workspaceType} />
            {quoteId && <MissedDealFeedback formId={tabId} />}
          </GenericErrorBoundary>
        }
        greeksFeesMetricsTabContent={
          <GenericErrorBoundary
            key={tabId}
            context={{ workspaceType: workspaceType, tabId: tabId, quoteId: quoteId ?? '' }}
          >
            <Status tabId={tabId} workspaceType={workspaceType} />
            <TradeWay tabId={tabId} workspaceType={workspaceType} />
            {quoteId && <GreeksFeesMetricsTabContent tabId={tabId} workspaceType={workspaceType} />}
          </GenericErrorBoundary>
        }
      />
    </ProductDumpApiContext.Provider>
  );
};

export const SidePanel = connectSidePanel(Component);
SidePanel.displayName = 'SidePanel';
